import {
  workbookCellRangeSchema,
  type WorkbookColumnType,
  type WorkbookDefinition,
  type WorkbookFreeformSheet,
} from '@erp/shared-types';
import { cellAddress, parseCellRange } from '@erp/workbooks';
import { tableNames, unusedName } from '../workbookNames';

/**
 * A new, empty freeform sheet, named `base` ("Freeform sheet", then
 * "Freeform sheet 2") among every table's names. It has no Result table
 * until its cells are chosen.
 */
export function newFreeformSheet(
  definition: Pick<WorkbookDefinition, 'sources' | 'sheets'>,
  id: string,
  base: string,
): WorkbookFreeformSheet {
  return {
    id,
    name: unusedName(tableNames(definition), base),
    folder: '',
    kind: 'freeform',
    cells: {},
  };
}

/** Its cells with `address` holding `input`, stored as typed; empty text clears the cell. */
export function withCell(
  sheet: WorkbookFreeformSheet,
  address: string,
  input: string,
): WorkbookFreeformSheet {
  const { [address]: _previous, ...rest } = sheet.cells;
  return {
    ...sheet,
    cells: input === '' ? rest : { ...rest, [address]: input },
  };
}

/**
 * Its Result table reading `range` (an A1 range, either corner first, or one
 * cell), keeping each column's type by position; null when `range` isn't one.
 */
export function withTableRange(
  sheet: WorkbookFreeformSheet,
  range: string,
): WorkbookFreeformSheet | null {
  const typed = range.trim().toUpperCase();
  const parsed = parseCellRange(
    typed.includes(':') ? typed : `${typed}:${typed}`,
  );
  if (!parsed) return null;
  const ordered = `${cellAddress(parsed.start)}:${cellAddress(parsed.end)}`;
  if (!workbookCellRangeSchema.safeParse(ordered).success) return null;
  const width = parsed.end.column - parsed.start.column + 1;
  return {
    ...sheet,
    table: {
      range: ordered,
      columns: (sheet.table?.columns ?? []).slice(0, width),
    },
  };
}

/** Its Result table's column at `index` declared as `type`; columns before it stay Text. */
export function withColumnType(
  sheet: WorkbookFreeformSheet,
  index: number,
  type: WorkbookColumnType,
): WorkbookFreeformSheet {
  if (!sheet.table) return sheet;
  const columns = [...sheet.table.columns];
  while (columns.length <= index) columns.push({ type: 'text' });
  columns[index] = { ...columns[index], type };
  return { ...sheet, table: { ...sheet.table, columns } };
}
